import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { api, ErroApi } from '../api/client'
import type { UsuarioSessao } from '../api/acesso'
import { useSessao } from '../auth/useSessao'
import { useContextoChatwoot, type ContextoChatwoot } from './useContextoChatwoot'

/**
 * Abre a sessão do painel embutido — **sem senha e sem formulário**.
 *
 * **Por que isto existe.** O cookie particionado (CHIPS) é indexado pelo site de
 * topo: entrar na aba do portal grava em `(portal, portal)` e o iframe dentro do
 * Chatwoot lê `(chatwoot, portal)`, que está vazia. A primeira tentativa foi
 * mostrar o formulário de login aqui dentro — funcionava, e é a forma clássica
 * do phishing. Recusada em 05/08, com razão.
 *
 * **Como funciona.** O `?inst=` na URL do app configurado no Chatwoot autentica
 * a **instância**; quem identifica a pessoa é o agente logado que a plataforma
 * entrega por `postMessage`.
 *
 * 🔴 **A assimetria que sustenta o desenho:** `event.origin` no `postMessage` é
 * preenchido pelo navegador e a página emissora **não consegue forjá-lo**. Já o
 * cabeçalho `Origin` de uma requisição HTTP não é fronteira de autorização —
 * quem não é navegador nem o envia. Por isso a validação de origem acontece dos
 * dois lados, mas só esta aqui é controle de verdade.
 *
 * A sessão que o servidor devolve vale **só para o acervo**, mesmo que a pessoa
 * seja administradora do portal.
 */
export function SessaoDoPainel({
  painel,
  children,
}: {
  /**
   * 🔴 Qual painel está pedindo a sessão (demanda 057).
   *
   * O servidor devolve **as permissões deste painel**, e só elas. Antes era um
   * conjunto fixo com `AcervoVer` — que já autorizava, de fato, todo o painel
   * de contexto.
   */
  painel: string
  children: (ctx: ContextoChatwoot) => ReactNode
}) {
  const { t } = useTranslation()
  const { estado, definir } = useSessao()
  const ctx = useContextoChatwoot()

  const [erro, setErro] = useState<string | null>(null)
  const [tentando, setTentando] = useState(false)

  // O token vem da URL configurada no Chatwoot, uma vez, por quem instala.
  const inst = new URLSearchParams(window.location.search).get('inst')

  const abrir = useCallback(async () => {
    if (!inst || !ctx.agente) return

    setErro(null)
    setTentando(true)

    try {
      const usuario = await api.post<UsuarioSessao>('/embed/sessao', {
        inst,
        agente: ctx.agente,
        painel,
      })

      definir(usuario)
    } catch (e) {
      // A mensagem do servidor é acionável — "peça para cadastrarem seu e-mail"
      // resolve; "proibido" faz o atendente chamar o suporte.
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null

      setErro(corpo?.detail ?? t('painelEmbutido.falhou'))
    } finally {
      setTentando(false)
    }
  }, [inst, ctx.agente, painel, definir, t])

  useEffect(() => {
    if (estado === 'anonimo' && inst && ctx.agente) void abrir()
  }, [estado, inst, ctx.agente, abrir])

  if (estado === 'verificando' || tentando) return <div className="aviso">{t('comum.carregando')}</div>

  if (estado === 'autenticado') return <>{children(ctx)}</>

  // A partir daqui a sessão não existe, e cada motivo pede uma frase diferente.

  if (!inst)
    return (
      <div className="aviso aviso--erro">
        <p>{t('painelEmbutido.semToken')}</p>
        <p className="contexto__dica">{t('painelEmbutido.semTokenDica')}</p>
      </div>
    )

  if (ctx.origem === 'aguardando') return <div className="aviso">{t('historico.aguardando')}</div>

  if (!ctx.agente)
    return (
      <div className="aviso aviso--erro">
        <p>{t('painelEmbutido.semAgente')}</p>
        <p className="contexto__dica">{t('painelEmbutido.semAgenteDica')}</p>
      </div>
    )

  return (
    <div className="aviso aviso--erro">
      <p>{erro ?? t('painelEmbutido.falhou')}</p>
    </div>
  )
}
